// Type definitions for the gtag function
interface GTagEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

// Ensure gtag is available in the global scope
declare global {
  interface Window {
    gtag: (
      command: 'event',
      eventName: string,
      eventParams?: GTagEvent | object
    ) => void;
  }
}

// Track when users search for a specific pub
export const trackPubSearch = (searchTerm: string, pubName?: string, pubAddress?: string) => {
  window.gtag('event', 'pub_search', {
    search_term: searchTerm,
    pub_name: pubName || 'not_found',
    pub_address: pubAddress || 'not_available',
  });
};

// Track when users search near their location
export const trackNearMeSearch = (latitude: number, longitude: number) => {
  window.gtag('event', 'search_near_me', {
    latitude,
    longitude,
  });
};

// Track when users click to navigate to the search area page
export const trackSearchAreaNavigation = () => {
  window.gtag('event', 'navigate_to_search_area');
};

// Track when users perform an actual search in a specific area
export const trackAreaSearch = (latitude: number, longitude: number, radius: number) => {
  window.gtag('event', 'search_area', {
    latitude,
    longitude,
    radius,
  });
};

// Track when users place a pin on the map
export const trackMapPinPlaced = (latitude: number, longitude: number) => {
  window.gtag('event', 'map_pin_placed', {
    latitude,
    longitude,
  });
};

// Track when users change the search radius
export const trackSearchRadiusChanged = (radius: number) => {
  window.gtag('event', 'search_radius_changed', {
    radius,
  });
};

// Track when users submit a pub garden
export const trackPubGardenSubmission = () => {
  window.gtag('event', 'submit_pub_garden');
};

// Track when users submit a new pub
export const trackNewPubSubmission = () => {
  window.gtag('event', 'submit_new_pub');
};

// Track when pub not found in database
export const trackPubNotFound = (pubName: string) => {
  window.gtag('event', 'pub_not_found', {
    pub_name: pubName,
  });
};

// Track when users view a pub page
export const trackPubView = (googlePlaceId: string, pubName: string, hasGardens: boolean, hasNoGarden: boolean) => {
  window.gtag('event', 'pub_view', {
    pub_id: googlePlaceId,
    pub_name: pubName,
    has_gardens: hasGardens,
    has_no_garden: hasNoGarden
  });
};

// Track when users leave a page with no garden
export const trackNoGardenBounce = (googlePlaceId: string, timeOnPage: number) => {
  window.gtag('event', 'no_garden_bounce', {
    pub_id: googlePlaceId,
    time_on_page: timeOnPage
  });
};

// Track how long users view gardens
export const trackGardenViewDuration = (googlePlaceId: string, gardenCount: number, duration: number) => {
  window.gtag('event', 'garden_view_duration', {
    pub_id: googlePlaceId,
    garden_count: gardenCount,
    duration: duration
  });
};

// Track which view type users select (2D or 3D)
export const trackViewTypeSelected = (googlePlaceId: string, viewType: string, gardenCount: number) => {
  window.gtag('event', 'view_type_selected', {
    pub_id: googlePlaceId,
    view_type: viewType,
    garden_count: gardenCount
  });
};

// Track when users start editing a garden
export const trackGardenEditStarted = (googlePlaceId: string, gardenCount: number) => {
  window.gtag('event', 'garden_edit_started', {
    pub_id: googlePlaceId,
    garden_count: gardenCount
  });
};

// Track when users initiate share functionality
export const trackShareInitiated = (googlePlaceId: string, pubName: string, hasGardens: boolean) => {
  window.gtag('event', 'share_initiated', {
    pub_id: googlePlaceId,
    pub_name: pubName,
    has_gardens: hasGardens
  });
};

// Track when users return to search
export const trackReturnToSearch = (googlePlaceId: string, hasGardens: boolean, timeOnPage: number) => {
  window.gtag('event', 'return_to_search', {
    pub_id: googlePlaceId,
    has_gardens: hasGardens,
    time_on_page: timeOnPage
  });
};

// Track 3D scene loading time
export const track3DSceneLoadingTime = (loadTime: number, googlePlaceId: string, meshCount: number) => {
  window.gtag('event', '3d_scene_loading_time', {
    load_time: loadTime,
    pub_id: googlePlaceId,
    mesh_count: meshCount
  });
};

// Track when search results lead to a pub view
export const trackSearchToViewConversion = (googlePlaceId: string, hasGarden: boolean, sortBy: string, filterSettings: object) => {
  window.gtag('event', 'search_to_view', {
    pub_id: googlePlaceId,
    has_garden: hasGarden,
    sort_by: sortBy,
    filter_settings: JSON.stringify(filterSettings)
  });
};

// Track when users apply filters to pub search results
export const trackFiltersApplied = (filterSettings: object) => {
  window.gtag('event', 'filters_applied', {
    filter_settings: JSON.stringify(filterSettings)
  });
};

// Track metrics about search results
export const trackSearchResultsMetrics = (
  totalCount: number, 
  withGardensCount: number, 
  lat: number, 
  lng: number, 
  radius: number
) => {
  window.gtag('event', 'search_results_metrics', {
    total_results: totalCount,
    with_gardens_count: withGardensCount,
    with_gardens_percentage: totalCount > 0 ? (withGardensCount / totalCount) * 100 : 0,
    latitude: lat,
    longitude: lng,
    radius: radius
  });
};

// Track whether users prefer map or list view
export const trackViewPreference = (viewType: string, resultCount: number) => {
  window.gtag('event', 'search_view_preference', {
    view_type: viewType,
    result_count: resultCount
  });
};

// Track when a garden is submitted after viewing a pub with no garden
export const trackGardenSubmissionConversion = (googlePlaceId: string, cameFromNoGarden: boolean) => {
  window.gtag('event', 'garden_submission_conversion', {
    pub_id: googlePlaceId,
    from_no_garden_view: cameFromNoGarden
  });
};

// Track when a pub is marked as having no garden
export const trackNoGardenMarked = (googlePlaceId: string, pubName: string, cameFromNoGarden: boolean) => {
  window.gtag('event', 'no_garden_marked', {
    pub_id: googlePlaceId,
    pub_name: pubName,
    from_no_garden_view: cameFromNoGarden
  });
};

// Track when a garden drawing is completed
export const trackGardenDrawingCompleted = (googlePlaceId: string, pointCount: number, isEdit: boolean, duration: number) => {
  window.gtag('event', 'garden_drawing_completed', {
    pub_id: googlePlaceId,
    point_count: pointCount,
    is_edit: isEdit,
    drawing_duration: duration
  });
};

// Track the type of garden submission (new vs update)
export const trackGardenSubmissionType = (googlePlaceId: string, submissionType: string, pointCount: number) => {
  window.gtag('event', 'garden_submission_type', {
    pub_id: googlePlaceId,
    submission_type: submissionType,
    point_count: pointCount
  });
};

// Track garden deletion events
export const trackGardenDeleted = (googlePlaceId: string, gardenId: string) => {
  window.gtag('event', 'garden_deleted', {
    pub_id: googlePlaceId,
    garden_id: gardenId
  });
};

// Track when garden uncertainty is toggled
export const trackUncertaintyToggled = (gardenId: string, isUncertain: boolean) => {
  window.gtag('event', 'uncertainty_toggled', {
    garden_id: gardenId,
    is_uncertain: isUncertain
  });
};

// Track when users view results after submission
export const trackViewResultsAfterSubmission = (googlePlaceId: string, timeSpentDrawing: number) => {
  window.gtag('event', 'view_results_after_submission', {
    pub_id: googlePlaceId,
    time_spent_drawing: timeSpentDrawing
  });
};

// Track performance of drawing tools
export const trackDrawingToolsPerformance = (initTime: number, meshCount: number) => {
  window.gtag('event', 'drawing_tools_performance', {
    initialization_time: initTime,
    mesh_count: meshCount
  });
};

// Track the method used to select a search area location
export const trackSearchAreaSelectionMethod = (method: string, latitude: number, longitude: number) => {
  window.gtag('event', 'search_area_selection', {
    selection_method: method,
    latitude: latitude,
    longitude: longitude
  });
};

// Track when users navigate away from the home page
export const trackHomePageExit = (destination: string) => {
  window.gtag('event', 'home_page_exit', {
    destination: destination
  });
}; 

// Track user authentication status
export const trackUserAuth = (isAuthenticated: boolean, authMethod?: string) => {
  window.gtag('event', 'user_authentication', {
    is_authenticated: isAuthenticated,
    auth_method: authMethod || 'unknown'
  });
};

// Track when a user submits a garden with authentication status
export const trackAuthenticatedGardenSubmission = (googlePlaceId: string, isAuthenticated: boolean, pointCount: number) => {
  window.gtag('event', 'authenticated_garden_submission', {
    pub_id: googlePlaceId,
    is_authenticated: isAuthenticated,
    point_count: pointCount
  });
};

// Track when a user views their profile
export const trackProfileView = (gardenCount: number, totalPoints: number) => {
  window.gtag('event', 'profile_view', {
    garden_count: gardenCount,
    total_points: totalPoints
  });
};

// Track when a user views the leaderboard
export const trackLeaderboardView = (topScore: number, userRank?: number) => {
  window.gtag('event', 'leaderboard_view', {
    top_score: topScore,
    user_rank: userRank || 'not_ranked'
  });
};